<template>
  <div class="p-sms-tab py-3">
    <div class="p-toolbar">
      <div class="p-toolbar-item p-search">
        <p-search-input
          v-model="search"
          :items="searchSuggestions"
          @submit="searchSms"
        >
        </p-search-input>
      </div>
      <div class="p-toolbar-item d-inline-flex align-center ml-6">
        <v-switch
          v-model="onlyThisProject"
          :label="$t('sms.onlyThisProject')"
          inset
          dense
          class="p-input-switch"
          @click.stop
          @change="reloadProjectSms"
        ></v-switch>
        <v-badge
          :value="unreadSms"
          icon="mdi-bell"
          overlap
          bordered
          offset-x="7"
          offset-y="7"
          class="ml-8 mt-n2"
        >
          <template v-slot:badge>
            {{ unreadSms }}
          </template>
          <v-tooltip bottom v-if="unreadSms">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                color="grey"
                dark
                v-bind="attrs"
                v-on="on"
              >
                mdi-bell
              </v-icon>
            </template>
            <span>{{ $t('sms.notificationUnreadSmsHint') }}</span>
          </v-tooltip>
        </v-badge>
      </div>
      <div v-if="hasAccess()" class="p-toolbar-item p-right">
        <v-btn
          class="p-btn-rounded mr-4"
          large
          rounded
          @click.stop="openNewSms()"
        >
          {{ $t("sms.newSms") }}
        </v-btn>
      </div>
    </div>
    <div class="v-main-wrap mt-1">
      <div v-if="smsLoaded && sms.length > 0">
        <v-col sm="3" class="px-0 py-0">
          <v-pagination
            v-if="smsLoaded"
            v-model="page"
            :length="pageCount"
            :total-visible="10"
            class="p-sms-tab__pagination"
            @input="nextPage"
          >
          </v-pagination>
        </v-col>
      </div>
      <v-divider></v-divider>
      <v-row>
        <v-col
          v-if="smsLoaded && sms.length > 0"
          cols="12"
          sm="6"
          md="5"
          xl="4"
          class="pr-0"
        >
          <v-list class="p-sms-tab__list">
            <v-list three-line class="p-sms-tab__main-list">
              <template v-for="(item, index) in smsComputed">
                <v-list-item-group v-bind:key="item.id">
                  <v-list-item
                    v-bind:key="item.id"
                    :class="[item.active ? 'p-sms-tab__sublist--selected' : 'p-sms-tab__sublist']"
                    @click="setData(item.id, item.active, index)"
                  >
                    <v-list-item-avatar class="ml-4">
                      <p-avatar
                        :email="item.source.email"
                        :char="item.source.name[0]"
                        :size="40"
                      >
                      </p-avatar>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="p-bold-text">
                        {{ item.source.name }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="p-sms-tab__body">
                        {{ item.message }}
                      </v-list-item-subtitle>
                      <div class="item-changed-wrapper-sms">
                        <v-tooltip v-if="item.changed" bottom>
                          <template v-slot:activator="{ on }">
                            <v-btn icon v-on="on">
                              <span v-if="item.changed" class="item-changed"></span>
                            </v-btn>
                          </template>
                          {{ $t('sms.changedTooltip') }}
                        </v-tooltip>
                      </div>
                    </v-list-item-content>
                    <v-list-item-action class="item-action mr-4">
                      <v-list-item-action-text
                        class="p-bold-text"
                        v-text="getTime(item.createdAt)"
                      ></v-list-item-action-text>
                    </v-list-item-action>
                    <v-tooltip bottom v-if="!item.isThreadRead">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          color="primary"
                          dark
                          v-bind="attrs"
                          v-on="on"
                          class="p-sms-tab__icon-unread-thread"
                        >
                          mdi-bell
                        </v-icon>
                      </template>
                      <span>{{ $t('sms.notificationUnreadSmsInThreadHint') }}</span>
                    </v-tooltip>
                  </v-list-item>
                </v-list-item-group>
                <v-divider :key="index"></v-divider>
              </template>
            </v-list>
          </v-list>
        </v-col>
        <v-col
          v-if="smsLoaded && sms.length > 0"
          cols="12"
          sm="6"
          md="7"
          xl="8"
          class="p-sms-tab__thread"
        >
          <label class="p-sms-tab__label" v-if="smsItem != null">
            {{ getDestinationText(smsItem[0].destinations) }}
          </label>
          <div v-if="smsItem == null" class="mt-8" />
          <div>
            <sms-item
              :smsItem="smsItem"
              :thread="smsThread"
              @isReadChanged="onSmsIsReadChanged"
              @delete-event="openDeleteDialog"/>
          </div>
        </v-col>
        <v-col
          v-if="smsLoaded && !search && sms.length <= 0"
          cols="4"
          md="12"
          class="mt-16"
        >
          <p-no-data
            :text="$t('sms.noSms')"
            :btn-text="$t('sms.newSms')"
            :hide-btn="!hasAccess()"
            img="chat.png"
            @btn-click="openNewSms()"
          >
          </p-no-data>
        </v-col>
      </v-row>
    </div>

    <send-dialog :dialog.sync="sendDialog"></send-dialog>

    <p-delete-dialog
      :dialog.sync="deleteDialog"
      :item="deletedSms"
      :item-data="deletedSmsData"
      :load-relations="loadRelations"
      :delete-item="deleteSms"
      @itemDeleted="onItemDeleted"
    ></p-delete-dialog>
  </div>
</template>


<script>
import { mapState, mapGetters, mapActions } from 'vuex'

import moment from 'moment'

import PAvatar from '@/components/PAvatar'
import PSearchInput from '@/components/PSearchInput'
import PNoData from '@/components/PNoData'
import PDeleteDialog from '@/components/PDeleteDialog'

import SendDialog from './SmsSendDialog'
import SmsItem from './SmsItem'

import snackbarMixin from '@/mixins/snackbar.mixin'
import pageMixin from '@/mixins/page.mixin'
import accessMixin from '@/mixins/access.mixin'

import eventBus from '@/eventBus'


export default {
  components: {
    PAvatar,
    PSearchInput,
    PNoData,
    PDeleteDialog,
    SmsItem,
    SendDialog
  },


  mixins: [
    snackbarMixin,
    pageMixin,
    accessMixin
  ],


  data () {
    return {
      smsItems: null,
      search: '',
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
      smsItem: null,
      smsLoaded: false,
      onlyThisProject: true,
      sendDialog: false,
      smsThread: null,
      smsIndexOld: null,
      unreadSms: null,
      deleteDialog: false,
      deletedSms: null,
      deletedSmsData: null
    }
  },


  computed: {
    ...mapState('sms', ['sms', 'smsTotalCount', 'threadSms']),
    ...mapGetters('sms', ['searchSuggestions']),
    ...mapGetters('project', ['currentProjectId']),
    ...mapGetters('changes', ['isItemChanged']),

    smsComputed () {
      return this.sms.map(sms => {
        return {
          ...sms,
          changed: this.isItemChanged(sms.id)
        }
      })
    }
  },


  watch: {
    'pagination.page': (newPage) => {
      this.reloadPage(newPage)
    },

    currentProjectId () {
      this.smsItems = null
      Promise.all([this.reloadPage()]).then(() => {
        this.setPagination()
      })
    }
  },


  methods: {
    ...mapActions('sms', ['loadSms', 'loadAllSms', 'loadThreadSms', 'loadRelations', 'deleteSms']),
    ...mapActions('notification', ['loadNotificationByProject']),
    ...mapActions('changes', ['removeFromChangedItems', 'removeFromChanges']),


    initEventBus () {
      eventBus.$on('goToEntity', async (entityTitle) => {
        await this.searchSms(entityTitle)
      })

      eventBus.$on('loadEntities', async () => {
        await this.reloadProjectSms()
      })
    },

    async onSmsIsReadChanged () {
      await this.getNotification()
      await this.loadSms({
        onlyThisProject: this.onlyThisProject,
        skip: (this.page - 1) * this.itemsPerPage,
        take: this.itemsPerPage
      })
    },

    async getNotification () {
      const params = {
        projectId: this.currentProjectId,
        onlyThisProject: this.onlyThisProject
      }
      const response = await this.loadNotificationByProject(params)
      this.unreadSms = response.data.unreadSms
    },

    openNewSms () {
      this.sendDialog = true
    },

    async setData (threadId, isActive, index) {
      console.info('setData ----------->')
      this.removeFromChanges(this.smsItems[index])
      if (!isActive) {
        this.clearOldSelected()
        this.smsItems[index].active = !isActive
        this.smsIndexOld = index
        this.startPageLoading()
        await this.loadThreadSms({
          threadId: threadId,
          onlyThisProject: this.onlyThisProject
        })
        this.smsItem = this.threadSms
        this.smsThread = threadId
        this.stopPageLoading()
      } else {
        this.smsItems[index].active = !isActive
        this.smsItem = null
        this.threadId = null
      }
    },

    clearOldSelected () {
      if (this.smsIndexOld != null) {
        this.smsItems.forEach(e => (e.active = false))
        this.smsIndexOld = null
      }
    },

    async searchSms (value) {
      await this.reloadPage(1, value)
      this.setPagination()
    },

    setPagination () {
      this.pageCount = Math.ceil(this.smsTotalCount / this.itemsPerPage)
      this.getNotification()
    },

    async reloadProjectSms () {
      this.page = 1
      await this.reloadPage()
      this.setPagination()
      await this.loadAllSms(this.onlyThisProject)
    },


    async reloadPage (page, search) {
      this.smsItems = null
      this.smsLoaded = false
      await this.nextPage()
    },

    async nextPage (page, search) {
      this.startPageLoading()
      this.smsItem = null
      this.smsIndexOld = null
      this.page = page || this.page
      await this.loadSms({
        onlyThisProject: this.onlyThisProject,
        skip: (this.page - 1) * this.itemsPerPage,
        take: this.itemsPerPage,
        search: search || this.search
      })
      this.smsLoaded = true
      this.smsItems = this.sms
      this.getNotification()
      this.stopPageLoading()
    },

    getTime (smsDate) {
      if (smsDate === undefined) {
        return null
      }
      return moment(smsDate).format('MMM D')
    },

    getDestinationText (destinations) {
      let destination = ''
      for (let i = 0; i < destinations.length; i++) {
        if (destination.length > 0) {
          destination += ', '
        }
        destination += destinations[i].name ? destinations[i].name : destinations[i].phoneNumber
      }
      return destination
    },

    async openDeleteDialog (sms) {
      try {
        if (sms) {
          this.deletedSms = sms
          this.deletedSmsData = {
            type: this.$t('entity.sms'),
            title: sms.message
          }
        }
        this.deleteDialog = true
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    onItemDeleted () {
      this.showSnackbarSuccess(this.$t('sms.smsDeleted'))
    }
  },


  async created () {
    this.initEventBus()
    try {
      await this.reloadPage()
      this.setPagination()
      await this.loadAllSms(this.onlyThisProject)
      if (this.$router.currentRoute.params?.goto) {
        await this.searchSms(this.$router.currentRoute.params?.goto)
      }
    } catch (error) {
      this.showSnackbarError()
      console.log(error)
      this.$log.error(error)
    }
  }
}
</script>
